import { useRef, useState } from 'react';

import useWebSocket from 'react-use-websocket';

import type { TvDashboard } from 'src/api/zrm';
import { clearTvToken, retryDelayMs, tvApiBaseUrl } from 'src/tv/tvClient';
import parseWsMessage from 'src/utils/parseWsMessage';

/** zrm `live_stream.CLOSE_UNPAIRED`: the token is unknown, revoked or for another dashboard. */
export const CLOSE_UNPAIRED = 4401;
// zrm pings every 25 s; a socket silent for longer is dead even if the browser has not noticed.
const HEARTBEAT = { interval: 25_000, timeout: 60_000 };

/** zrm `live_stream.ping`, with zrm's clock. */
interface LivePing {
  type: 'ping';
  at: string;
}

export interface LiveSocket {
  /** Since when the screen is offline, or null while connected. */
  offlineSince: number | null;
  /** zrm's clock minus the TV's: a TV's clock can be minutes off. */
  clockOffsetMs: number;
}

const liveUrl = (path: string): string => `${tvApiBaseUrl().replace(/\/$/, '').replace(/^http/, 'ws')}${path}`;

/**
 * A live wall screen's zrm WebSocket (zrm `routers/tv/ws.py`). Sends the TV token as the first message and hands
 * every message but zrm's ping to `onMessage`. zrm sends its backlog before the first ping, so a message is backlog
 * until this socket's first ping. Reconnects forever on `retryDelayMs`; a close with `CLOSE_UNPAIRED` forgets the
 * token and calls `onUnpaired` instead.
 */
export const useTvLiveSocket = <M extends { type: string }>(
  dashboard: TvDashboard,
  path: string,
  token: string,
  onUnpaired: () => void,
  onMessage: (message: M, isBacklog: boolean) => void
): LiveSocket => {
  const [offlineSince, setOfflineSince] = useState<number | null>(null);
  const [clockOffsetMs, setClockOffsetMs] = useState(0);
  const failures = useRef(0);
  const isBacklog = useRef(true);

  useWebSocket(liveUrl(path), {
    onOpen: (event) => {
      isBacklog.current = true;
      // Before the heartbeat starts, so zrm reads the token first.
      (event.target as WebSocket).send(JSON.stringify({ token }));
    },
    onMessage: (event) => {
      // zrm answers an accepted token at once, so any message means connected.
      failures.current = 0;
      setOfflineSince(null);
      const message = parseWsMessage<M | LivePing>(event);

      if (!message) return;

      if (message.type === 'ping') {
        isBacklog.current = false;
        setClockOffsetMs(Date.parse((message as LivePing).at) - Date.now());

        return;
      }

      onMessage(message as M, isBacklog.current);
    },
    onClose: (event) => {
      if (event.code === CLOSE_UNPAIRED) {
        clearTvToken(dashboard, token);
        onUnpaired();

        return;
      }

      setOfflineSince((since) => since ?? Date.now());
    },
    shouldReconnect: (event) => event.code !== CLOSE_UNPAIRED,
    // The default gives up after 20 tries; a TV has nobody to reload it.
    reconnectAttempts: Infinity,
    // Counted here, not by the library: it resets on open, and zrm may accept a socket and then close it.
    reconnectInterval: () => retryDelayMs(failures.current++),
    heartbeat: HEARTBEAT,
    // Messages go to `onMessage`; no re-render per message, as the backlog can be 500.
    filter: () => false,
  });

  return { offlineSince, clockOffsetMs };
};
